// HTTP Status Code Error Messages
export const ERROR_MESSAGES = {
  // Client Errors (4xx)
  400: "Bad Request. Please check your input and try again.",
  401: "Session expired. Please login again.",
  403: "You do not have permission to perform this action.",
  404: "The requested resource was not found.",
  408: "Request timeout. Please try again.",
  409: "A conflict occurred. The resource may already exist.",
  413: "File or data is too large. Please reduce the size and try again.",
  415: "Unsupported file type or format.",
  422: "Invalid data provided. Please check your input.",
  429: "Too many requests. Please wait a moment and try again.",

  // Server Errors (5xx)
  500: "Internal server error. Please try again later.",
  501: "This feature is not currently supported.",
  502: "Server is temporarily unavailable. Please try again.",
  503: "Service temporarily unavailable. Please try again later.",
  504: "Server timeout. Please try again.",

  // Network Errors
  NETWORK_ERROR: "Network error. Please check your internet connection.",
  TIMEOUT: "Request timed out. Please try again.",

  // Default
  DEFAULT: "An unexpected error occurred. Please try again.",
};

// Get user-friendly error message based on status code
export const getErrorMessage = (error) => {
  if (!error) return ERROR_MESSAGES.DEFAULT;

  // Get status code first
  const status = error.response?.status;

  // Check if we have a custom error message from the server
  const serverMessage =
    error.response?.data?.message ||
    error.response?.data?.error ||
    error.response?.data?.msg;

  // For certain status codes, prefer server message if available
  if (serverMessage && [400, 409, 422].includes(status)) {
    return serverMessage;
  }

  // Return predefined message based on status code
  if (status && ERROR_MESSAGES[status]) {
    return ERROR_MESSAGES[status];
  }

  // Special handling for CORS-blocked errors
  // When CORS blocks the response, we can't access status code
  // But we can check the error code and message for clues
  if (!error.response && error.code === "ERR_NETWORK") {
    const errorMsg = error.message?.toLowerCase() || "";

    // Check for 413 in error message
    if (
      errorMsg.includes("413") ||
      errorMsg.includes("too large") ||
      errorMsg.includes("entity too large") ||
      errorMsg.includes("request entity")
    ) {
      return ERROR_MESSAGES[413];
    }

    // For ERR_NETWORK with no response, it's likely a payload size issue
    // Check if request has large data
    const requestData = error.config?.data;
    if (requestData) {
      const dataSize =
        typeof requestData === "string"
          ? requestData.length
          : JSON.stringify(requestData).length;

      // If data is larger than 10MB, assume it's a 413
      if (dataSize > 10 * 1024 * 1024) {
        return ERROR_MESSAGES[413];
      }
    }

    // Generic network error for ERR_NETWORK
    return ERROR_MESSAGES.NETWORK_ERROR;
  }

  // Check for timeout
  if (
    error.code === "ECONNABORTED" ||
    error.message?.toLowerCase().includes("timeout")
  ) {
    return ERROR_MESSAGES.TIMEOUT;
  }

  // Network error (no response from server) - only if no status code
  if (!error.response && !status) {
    return ERROR_MESSAGES.NETWORK_ERROR;
  }

  // Default fallback
  return ERROR_MESSAGES.DEFAULT;
};
